(() => {
  const ROOT_SELECTOR = '[data-category-router] .screwman-category-grid';
  const CARD_SELECTOR = '.screwman-category-card[href*="/collections/"]';
  const CACHE_PREFIX = 'screwman-category-products-v3:';
  const MAX_CONCURRENT = 6;

  const normalisePath = (href) => {
    try {
      return new URL(href, window.location.origin).pathname.replace(/\/$/, '');
    } catch (_) {
      return '';
    }
  };

  const titleFor = (card) =>
    card.querySelector('.screwman-category-label strong')?.textContent?.trim() ||
    card.textContent.trim();

  const hideCard = (card) => {
    card.hidden = true;
    card.style.setProperty('display', 'none', 'important');
    card.setAttribute('aria-hidden', 'true');
  };

  const showCard = (card) => {
    card.hidden = false;
    card.style.removeProperty('display');
    card.removeAttribute('aria-hidden');
  };

  const readCached = (path) => {
    try {
      const value = sessionStorage.getItem(CACHE_PREFIX + path);
      return value === null ? null : value === 'true';
    } catch (_) {
      return null;
    }
  };

  const writeCached = (path, hasProducts) => {
    try {
      sessionStorage.setItem(CACHE_PREFIX + path, String(hasProducts));
    } catch (_) {
      // The live check still works when sessionStorage is unavailable.
    }
  };

  const sortGrid = (grid) => {
    Array.from(grid.children)
      .filter((child) => child.matches('.screwman-category-card') && !child.hidden)
      .sort((a, b) =>
        titleFor(a).localeCompare(titleFor(b), undefined, {
          sensitivity: 'base',
          numeric: true,
        })
      )
      .forEach((card) => grid.appendChild(card));
  };

  const prepare = () => {
    const grouped = new Map();
    const fastenersPage =
      window.location.pathname.replace(/\/$/, '') === '/collections/fasteners';
    const excludedFastenerCards = new Set([
      '/collections/bolts',
      '/collections/default',
    ]);

    document.querySelectorAll(ROOT_SELECTOR).forEach((grid) => {
      Array.from(grid.querySelectorAll(CARD_SELECTOR)).forEach((card) => {
        const path = normalisePath(card.href);
        if (!path) return;

        if (fastenersPage && excludedFastenerCards.has(path)) {
          card.remove();
          return;
        }

        const shownCount = card.querySelector('.screwman-category-label span');
        if (shownCount && /^\s*0\s+products?\b/i.test(shownCount.textContent || '')) {
          hideCard(card);
          writeCached(path, false);
          return;
        }

        const cached = readCached(path);
        if (cached !== null) {
          cached ? showCard(card) : hideCard(card);
        }

        if (!grouped.has(path)) grouped.set(path, []);
        grouped.get(path).push(card);
      });

      sortGrid(grid);
    });

    return grouped;
  };

  const verifyCollections = async (grouped) => {
    const queue = [];

    grouped.forEach((cards, path) => {
      const cached = readCached(path);
      if (cached !== null) return;
      queue.push({ cards, path });
    });

    let cursor = 0;
    const worker = async () => {
      while (cursor < queue.length) {
        const item = queue[cursor++];
        try {
          const response = await fetch(`${item.path}/products.json?limit=1`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });

          if (response.status === 404) {
            writeCached(item.path, false);
            item.cards.forEach(hideCard);
            continue;
          }
          if (!response.ok) continue;

          const data = await response.json();
          if (!data || !Array.isArray(data.products)) continue;

          const hasProducts = data.products.length > 0;
          writeCached(item.path, hasProducts);
          item.cards.forEach(hasProducts ? showCard : hideCard);
        } catch (_) {
          // Leave the card visible if Shopify cannot complete the live check.
        }
      }
    };

    await Promise.allSettled(
      Array.from({ length: Math.min(MAX_CONCURRENT, queue.length) }, () => worker())
    );

    document.querySelectorAll(ROOT_SELECTOR).forEach(sortGrid);
  };

  const run = () => verifyCollections(prepare());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', run);
})();
